// Centralized theme utility functions
export const themeClasses = {
  // Background classes
  bgPrimary: 'bg-gray-50 dark:bg-slate-900',          // Light gray in light mode (matches login)
  bgSecondary: 'bg-gray-100 dark:bg-slate-800',       // Medium gray
  bgTertiary: 'bg-gray-200 dark:bg-slate-700',        // Darker gray
  
  // Text classes
  textPrimary: 'text-gray-900 dark:text-white',
  textSecondary: 'text-gray-700 dark:text-gray-300',
  textTertiary: 'text-gray-500 dark:text-gray-400',
  textMuted: 'text-gray-400 dark:text-gray-500',
  
  // Border classes
  borderPrimary: 'border-gray-200 dark:border-gray-700',
  borderSecondary: 'border-gray-300 dark:border-gray-600',
  
  // Input/Form classes
  input: 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border-gray-200 dark:border-gray-600',
  inputFocus: 'focus:bg-white dark:focus:bg-gray-600',
  
  // Button classes  
  button: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600',
  buttonHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
  
  // Card/Surface classes
  card: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
  surface: 'bg-gray-50 dark:bg-gray-700',
  
  // Modal/Overlay classes
  modal: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
  overlay: 'bg-black bg-opacity-50 dark:bg-opacity-70',
};

// Utility function to combine theme classes
export function getThemeClasses(...classKeys: (keyof typeof themeClasses)[]): string {
  return classKeys.map(key => themeClasses[key]).join(' ');
}

// Common component theme combinations
export const componentThemes = {
  postCard: `${themeClasses.card} rounded-lg shadow-sm border mb-4`,
  commentBubble: `${themeClasses.surface} rounded-lg p-3`,
  formInput: `${themeClasses.input} ${themeClasses.inputFocus} border rounded-xl focus:ring-2 focus:ring-healthy-green focus:border-healthy-green transition-colors`,
  button: `${themeClasses.button} px-3 py-1.5 rounded-lg text-sm font-medium transition-colors`,
  modal: `${themeClasses.modal} rounded-lg shadow-xl max-w-2xl w-full overflow-hidden`,
  sidebar: `${themeClasses.card} w-64 rounded-lg shadow-sm border p-6`,
  header: `${themeClasses.bgPrimary} shadow-sm border-b ${themeClasses.borderPrimary}`,
};

// Theme-aware utility for conditional classes
export function themeConditional(lightClass: string, darkClass: string): string {
  return `${lightClass} dark:${darkClass}`;
}

// Generate consistent theme classes for common patterns
export function generateThemeClass(baseClasses: string, themeType: 'surface' | 'text' | 'border' | 'input' = 'surface'): string {
  const themeMap = {
    surface: themeClasses.bgPrimary,
    text: themeClasses.textPrimary, 
    border: themeClasses.borderPrimary,
    input: themeClasses.input
  };
  
  return `${baseClasses} ${themeMap[themeType]}`;
}